import { Suspense, useEffect, useState } from 'react';
import { isArray } from 'lodash';
import {
  Option,
  SelectWithSearch,
} from '../common/components/Inputs/SelectWithSearch/SelectWithSearch';
import { useFieldsAuditTrail, useUsersAuditTrail } from './hooks/useAuditTrail';
import { MultiSelect } from '../common/components/Inputs/MultiSelect/MultiSelect';
import { RefetchQueryButton } from '../common/components/Queries/RefetchQueryButton';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Stack from '@mui/material/Stack';
import { AutoHideSnackbar } from '../common/Snackbar/AutoHideSnackbar';
import { AlertTitle } from '../common/Snackbar/AlertTitle';
import Typography from '@mui/material/Typography';
import { ContentBox } from '../common/components/AppLayout/ContentBox';
import { TableSkeleton } from '../common/components/Table/TableSkeleton';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { downloadAuditTrail } from './auditTrailApi/auditTrailApi';
import { AuthError } from '../auth/utils';
import { AuditTrailTable } from './components/AuditTrailTable';
import {
  DateRangePickerMui,
  DateRangeValuePiece,
} from '../common/components/Inputs/DateRangePickerMui/DateRangePickerMui';
import { endOfDay } from 'date-fns';
import {
  basicErrorHandling,
  parseDateEndOfDayToRequest,
  parseDateToRequest,
} from '../common/components/Inputs/DateRangePickerMui/DateRangePickerMui.util';
import {
  TableLayout,
  TableLayoutActions,
  TableLayoutFilters,
  TableLayoutTable,
} from 'src/common/components/Layouts/TableLayout/TableLayout';
import Portal from '@mui/material/Portal';
import { pxToRem } from 'src/common/utils/fonts';

type ExportError = {
  title: string;
  description: string;
};

export const AuditTrail = () => {
  const { t } = useTranslation();
  const { iccid } = useParams();

  const [dateRange, setDateRange] = useState<[DateRangeValuePiece, DateRangeValuePiece]>([
    null,
    null,
  ]);

  const [selectedUsername, setSelectedUsername] = useState<Option | null>(null);
  const [selectedField, setSelectedField] = useState<string[]>([]);

  const { users: usersAuditTrail = [] } = useUsersAuditTrail(undefined, iccid);
  const { fields = [] } = useFieldsAuditTrail();

  const fieldsOptions = fields?.map((field: string) => ({
    label: field,
    value: field,
  }));

  const usernameOptions = usersAuditTrail
    ?.filter((user: string) => !!user)
    .map((user: string, index: number) => ({
      label: user,
      id: index,
    }));

  const { mutateAsync: exportAuditTrail, isPending: isExporting } = useMutation({
    mutationKey: ['AuditTrail download'],
    mutationFn: async () => {
      await downloadAuditTrail(
        iccid!,
        selectedUsername?.label as string,
        parseDateToRequest(dateRange?.[0]),
        parseDateEndOfDayToRequest(dateRange?.[1]),
        selectedField,
      );
    },
    retry: 0,
    onError: (error) => {
      if (error instanceof AuthError) {
        throw error;
      } else {
        setExportError({
          title: t('common.exportFailed'),
          description: error.message,
        });
      }
    },
  });

  const [exportError, setExportError] = useState<ExportError | null>(null);

  const [downloadingSnackbarOpened, setDownloadingSnackbarOpened] = useState(false);
  useEffect(() => {
    if (isExporting) {
      setDownloadingSnackbarOpened(true);
    }
  }, [isExporting]);

  return (
    <>
      <TableLayout>
        <TableLayoutFilters data-testid="auditTrailsFilters">
          <DateRangePickerMui
            disableFuture={true}
            onChange={(value) => {
              setDateRange([
                value && isArray(value) && value[0] ? value[0] : null,
                value && isArray(value) && value[1] ? endOfDay(value[1]) : null,
              ]);
            }}
            value={dateRange}
            allowClearing={true}
            onError={(err) => {
              setDateRange((prevDateRange) => {
                if (
                  (prevDateRange[0] && err[0] === null) ||
                  (prevDateRange[1] && err[1] === null)
                ) {
                  const range = basicErrorHandling(prevDateRange, err);

                  return [range[0], range[1] ? endOfDay(range[1]) : range[1]];
                }

                return prevDateRange;
              });
            }}
          />
          <MultiSelect
            data-testid={'field-multiselect'}
            showSelectAll={true}
            variant="text"
            options={fieldsOptions || []}
            value={selectedField}
            onChange={(val) => {
              setSelectedField(val);
            }}
            placeholder={t('common.field')}
            sx={{
              minWidth: pxToRem(170),
              maxWidth: pxToRem(170),
              '& > .MuiTextField-root': { height: '100%' },
            }}
          />
          <SelectWithSearch
            data-testid={`username-select`}
            placeholder={t('simInventory.userName')}
            options={usernameOptions || []}
            value={selectedUsername ?? null}
            onChange={(value) => setSelectedUsername(value)}
            sx={{ width: pxToRem(170) }}
          />
        </TableLayoutFilters>
        <TableLayoutActions data-testid="auditTrailsActionButtons">
          <RefetchQueryButton queryKey={['AuditTrail']} />
          <Button
            startIcon={<FileDownloadIcon sx={{ width: pxToRem(16), height: pxToRem(16) }} />}
            disabled={isExporting}
            color="secondary"
            onClick={() => exportAuditTrail()}
          >
            {t('common.export')}
          </Button>
        </TableLayoutActions>
        <TableLayoutTable>
          <Suspense
            fallback={
              <ContentBox>
                <TableSkeleton showStartSkeleton showEndSkeleton numberOfRows={20} />
              </ContentBox>
            }
          >
            <AuditTrailTable
              iccid={iccid!}
              dateRange={dateRange}
              selectedField={selectedField}
              selectedUsername={selectedUsername}
            />
          </Suspense>
        </TableLayoutTable>
      </TableLayout>
      <Portal>
        <Stack>
          <AutoHideSnackbar
            severity="success"
            open={downloadingSnackbarOpened}
            onClose={() => {
              setDownloadingSnackbarOpened(false);
            }}
          >
            <AlertTitle> {t('common.downloading')}!</AlertTitle>
          </AutoHideSnackbar>
          <AutoHideSnackbar
            open={!!exportError}
            severity="error"
            onClose={() => setExportError(null)}
          >
            <AlertTitle>{exportError?.title || ''}!</AlertTitle>
            <Typography variant="text1" color="text.white">
              {exportError?.description}
            </Typography>
          </AutoHideSnackbar>
        </Stack>
      </Portal>
    </>
  );
};
